import { constants } from '../common/constants/constants';
import { routesPath } from '../common/constants/routesPath';
import { REFRESH_TOKEN, TOKEN, USER_ROLES } from '../common/constants/localStorage';
import { IMPERSONATION_STORAGE_KEYS } from '../common/constants/impersonation';

/**
 * Per-tab state of a read-only impersonation (#790). The tab is marked with a sticky sessionStorage flag, and while
 * it is set the auth tokens live in sessionStorage only (see tokenService), so a consumer signed in on the same
 * browser is never read, overwritten or signed out. The API is the security control; this keeps the sessions apart.
 */

// the one-time handoff code, kept in memory only: it never reaches storage or the address bar after the pre-init
let handoffCode = null;
// set once the tab is leaving: from then on nothing may be sent, and nothing may fall back to localStorage
let impersonationEnded = false;
// once this page has been an impersonation tab it stays one until it reloads, even if the storage flag is missing
let tabSeen = false;

const UNSUBSCRIBE_TAB_KEY = 'unsubscribeTab';

const TAB_SCOPED_KEYS = [TOKEN, REFRESH_TOKEN, USER_ROLES, IMPERSONATION_STORAGE_KEYS.SESSION];

/**
 * Synchronous pre-init, run by impersonationPreInit.js (the first import of index.js) before i18n, analytics and
 * React. On /impersonate it flags the tab and moves the code from the URL fragment into memory, so it is gone from
 * the address bar and history at once.
 */
export const consumeHandoffFragment = () => {
    // a trailing slash (added by a proxy or a link rewriter) or another letter case is still the handoff page,
    // as the router matches paths case-insensitively
    if (window.location.pathname.replace(/\/+$/, '').toLowerCase() !== routesPath.PUBLIC.IMPERSONATE) {
        return;
    }

    // the code leaves the address bar first, whatever happens to the storage write below
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    handoffCode = fragment.get('code');
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);

    tabSeen = true;
    try {
        sessionStorage.setItem(IMPERSONATION_STORAGE_KEYS.TAB, '1');
    } catch (error) {
        // blocked storage: the tab still acts as an impersonation tab until it reloads (tabSeen)
    }
};

export const getHandoffCode = () => handoffCode;

export const isImpersonationEnded = () => impersonationEnded;

export const isImpersonationTab = () => {
    if (impersonationEnded || tabSeen) {
        return true;
    }

    try {
        tabSeen = sessionStorage.getItem(IMPERSONATION_STORAGE_KEYS.TAB) === '1';
    } catch (error) {
        tabSeen = false;
    }
    return tabSeen;
};

// a trailing slash or another letter case is still the unsubscribe page (the router matches case-insensitively)
export const isUnsubscribePath = (pathname = window.location.pathname) =>
    pathname.replace(/\/+$/, '').toLowerCase() === routesPath.PUBLIC.UNSUBSCRIBE;

// set by unsubscribePreInit.js on /unsubscribe: analytics then stays off for the rest of the tab (it fails closed,
// so a later page in this tab, or a reload, never reports the visit either)
let unsubscribeTabSeen = false;

export const markUnsubscribeTab = () => {
    unsubscribeTabSeen = true;
    try {
        sessionStorage.setItem(UNSUBSCRIBE_TAB_KEY, '1');
    } catch (error) {
        // blocked storage: the module flag still keeps analytics off until the page reloads
    }
};

const isUnsubscribeTab = () => {
    if (unsubscribeTabSeen) {
        return true;
    }
    try {
        return sessionStorage.getItem(UNSUBSCRIBE_TAB_KEY) === '1';
    } catch (error) {
        return false;
    }
};

// analytics and the support chat would report the impersonated user to third parties; analytics would also report
// the visit to an emailed unsubscribe link
export const shouldLoadThirdPartyScripts = () => !isImpersonationTab() && !isUnsubscribeTab();

// the support chat goes by the page shown now: it stays off on /unsubscribe, and comes back on the next page
export const shouldLoadSupportChat = (pathname = window.location.pathname) =>
    !isImpersonationTab() && !isUnsubscribePath(pathname);

export const getImpersonationSession = () => {
    try {
        return JSON.parse(sessionStorage.getItem(IMPERSONATION_STORAGE_KEYS.SESSION));
    } catch (error) {
        return null;
    }
};

export const startImpersonationTab = ({ accessToken, expiresAt, serverNow, target, actor }) => {
    tabSeen = true;
    sessionStorage.setItem(IMPERSONATION_STORAGE_KEYS.TAB, '1');
    TAB_SCOPED_KEYS.forEach(key => sessionStorage.removeItem(key));
    sessionStorage.setItem(TOKEN, JSON.stringify(accessToken));
    sessionStorage.setItem(
        IMPERSONATION_STORAGE_KEYS.SESSION,
        JSON.stringify({ expiresAt, serverNow, receivedAt: Date.now(), target, actor })
    );
};

/**
 * Leaves the impersonation with a full reload into the dealer app (never an SPA navigation, so no state of the
 * impersonated user survives). Every caller (401, countdown, Stop) may race here; the redirect is the same.
 */
export const endImpersonation = reason => {
    impersonationEnded = true;

    TAB_SCOPED_KEYS.forEach(key => sessionStorage.removeItem(key));
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEYS.TAB);

    window.location.replace(`${constants.DEALER_BASE_URL}/impersonation?ended=${encodeURIComponent(reason)}`);
};

// test hook: module state survives between tests otherwise
export const resetImpersonationModuleState = () => {
    handoffCode = null;
    impersonationEnded = false;
    tabSeen = false;
    unsubscribeTabSeen = false;
};

/** Time left on the server clock: the client clock is corrected by the offset measured when the token arrived. */
export const getRemainingMs = (session, now = Date.now()) => {
    const clockOffset = new Date(session.serverNow).getTime() - session.receivedAt;

    return new Date(session.expiresAt).getTime() - (now + clockOffset);
};
